
import React, { useState } from 'react';
import { CampaignFormData, SharingSettings } from '../types';
import SharingControls from './SharingControls';
import { Button } from './ui/button';
import { Share2 } from 'lucide-react';

interface CampaignSummaryProps {
  formData: CampaignFormData;
  isComplete: boolean;
  onShowSummary: () => void;
  campaignId?: string;
  onSharingUpdate?: (settings: SharingSettings) => void;
}

const CampaignSummary: React.FC<CampaignSummaryProps> = ({
  formData,
  isComplete,
  onShowSummary,
  campaignId,
  onSharingUpdate
}) => {
  const [showSharing, setShowSharing] = useState(false);

  return (
    <div className="space-y-4">
      <div className="bg-white p-6 rounded-lg shadow-lg border-l-4 border-red-600">
        <div className="border border-red-600 p-3 mb-4">
          <h2 className="text-lg font-bold text-red-700 tracking-wider text-center">
            CAMPAIGN SUMMARY
          </h2>
        </div>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-semibold text-neutral-700 text-sm uppercase tracking-wide">Campaign Name</h3>
            <p className="text-neutral-900 font-medium">{formData.core_issue_details.campaign_name}</p>
          </div>
          
          <div>
            <h3 className="font-semibold text-neutral-700 text-sm uppercase tracking-wide">Campaign Type</h3>
            <p className="text-neutral-900">{formData.core_issue_details.campaign_type}</p>
          </div>
          
          <div>
            <h3 className="font-semibold text-neutral-700 text-sm uppercase tracking-wide">Political Issue</h3>
            <p className="text-neutral-900">{formData.core_issue_details.political_issue}</p>
          </div>
          
          <div>
            <h3 className="font-semibold text-neutral-700 text-sm uppercase tracking-wide">Target Entity</h3>
            <p className="text-neutral-900">{formData.core_issue_details.target_entity}</p>
          </div>
          
          <div>
            <h3 className="font-semibold text-neutral-700 text-sm uppercase tracking-wide">Winning Aspiration</h3>
            <p className="text-neutral-900">{formData.campaign_outcome.winning_aspiration}</p>
          </div>
          
          {isComplete && (
            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <h3 className="font-bold text-green-800 mb-2 flex items-center">
                <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                Analysis Complete!
              </h3>
              <p className="text-sm text-green-700 mb-4">
                Your strategic analysis is ready. All reports are now available for review.
              </p>
              <div className="space-y-2">
                <button
                  onClick={onShowSummary}
                  className="w-full px-4 py-3 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors font-semibold"
                >
                  View Complete Analysis
                </button>
                
                {campaignId && (
                  <Button
                    onClick={() => setShowSharing(!showSharing)}
                    variant="outline"
                    className="w-full"
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    {showSharing ? 'Hide' : 'Share'} Campaign
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {showSharing && campaignId && (
        <SharingControls
          campaignId={campaignId}
          currentSettings={formData.sharing_settings}
          campaignName={formData.core_issue_details.campaign_name}
          onUpdate={(settings) => {
            onSharingUpdate?.(settings);
            setShowSharing(false);
          }}
        />
      )}
    </div>
  );
};

export default CampaignSummary;
